import { useEffect, type RefObject } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import type { PracticeFieldToneOption } from './ToneDropdownField';

interface ToneDropdownMenuProps {
    options: PracticeFieldToneOption[];
    current: string;
    /**
     * Wraps both the trigger and this menu (see ToneDropdownField) - the
     * outside-click check below tests against this, not a menu-only ref, so
     * the mousedown that closes the menu and the trigger's own click can
     * never race into an immediate re-open.
     */
    containerRef: RefObject<HTMLDivElement>;
    onSelect: (value: string) => void;
    onClose: () => void;
    align?: 'left' | 'right';
}

/**
 * Popover option list for ToneDropdownField - same outside-click/Escape close
 * and "each option wears its own tone, current one checked" behavior as
 * work-queue/priority/PriorityMenu.tsx's option list (minus that menu's
 * extreme-reason stage, which this field has no equivalent of).
 */
export function ToneDropdownMenu({
    options,
    current,
    containerRef,
    onSelect,
    onClose,
    align = 'left'
}: ToneDropdownMenuProps) {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [containerRef, onClose]);

    const alignClass = align === 'right' ? 'right-0' : 'left-0';

    return (
        <div
            role="listbox"
            className={`absolute top-full mt-1 ${alignClass} z-50 w-[200px] bg-white border border-gray-200 rounded-[10px] shadow-[0_10px_28px_rgba(0,0,0,0.13)] py-1`}
            onClick={(e) => e.stopPropagation()}
        >
            {options.map((option) => {
                const isSelected = option.value === current;
                const Icon = option.icon;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => onSelect(option.value)}
                        className="w-full flex items-center px-3 py-2 hover:bg-gray-50 gap-2"
                    >
                        <span className="flex-shrink-0 flex items-center justify-center w-4 h-4">
                            {Icon && (
                                <Icon
                                    className={`h-3.5 w-3.5 ${option.menuIconClasses ?? ''}`}
                                    aria-hidden="true"
                                />
                            )}
                        </span>
                        <span
                            className={`flex-1 text-left text-[13px] font-semibold ${option.menuLabelClasses}`}
                        >
                            {option.label}
                        </span>
                        {isSelected && (
                            <CheckIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        )}
                    </button>
                );
            })}
        </div>
    );
}
